import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Parse from 'parse';
import CryptoJS from 'crypto-js';
import {
  Box,
  Button,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Checkbox,
  FormControlLabel,
  Card,
  CardContent,
  Alert
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const SECRET_KEY = process.env.REACT_APP_SECRET_KEY || 'your-secret-key';

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    usernameOrEmail: '',
    password: '',
    rememberMe: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const currentUser = Parse.User.current();
    if (currentUser) {
      navigate('/');
      return;
    }

    // "Remember me": prefill the form from saved credentials on load.
    const rememberedUsername = localStorage.getItem('rememberedUsername');
    const rememberedPassword = localStorage.getItem('rememberedPassword');
    if (rememberedUsername) {
      let password = '';
      if (rememberedPassword) {
        try {
          password = CryptoJS.AES.decrypt(rememberedPassword, SECRET_KEY).toString(
            CryptoJS.enc.Utf8
          );
        } catch (error) {
          password = '';
        }
      }
      setFormData((prev) => ({
        ...prev,
        usernameOrEmail: rememberedUsername,
        password,
        rememberMe: true
      }));
    }
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value, checked } = e.target;

    let updatedFormData = {
      ...formData,
      [name]: name === 'rememberMe' ? checked : value
    };

    // Auto-fill password when email matches saved username
    if (name === 'usernameOrEmail') {
      const rememberedUsername = localStorage.getItem('rememberedUsername');
      const rememberedPassword = localStorage.getItem('rememberedPassword');

      if (value === rememberedUsername && rememberedPassword) {
        try {
          const decryptedPassword = CryptoJS.AES.decrypt(rememberedPassword, SECRET_KEY).toString(
            CryptoJS.enc.Utf8
          );

          updatedFormData = {
            ...updatedFormData,
            password: decryptedPassword,
            rememberMe: true
          };
        } catch (error) {
          console.error('Error decrypting password:', error);
        }
      } else if (value === '') {
        // Clear password if email is cleared
        updatedFormData = {
          ...updatedFormData,
          password: '',
          rememberMe: false
        };
      }
    }

    setFormData(updatedFormData);

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.usernameOrEmail.trim()) {
      newErrors.usernameOrEmail = 'Username or Email is required';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setLoading(true);
      setErrorMessage('');

      try {
        if (!Parse.Cloud) {
          throw new Error('Parse is not initialized. Please refresh the page.');
        }

        const response = await Parse.Cloud.run('operatorLogin', {
          username: formData.usernameOrEmail,
          password: formData.password
        });

        if (!response || !response.operator) {
          throw new Error('Invalid response from server');
        }

        // Store operator data
        localStorage.setItem('operatorId', response.operator.objectId);
        localStorage.setItem('operatorName', response.operator.name);
        localStorage.setItem('operatorEmail', response.operator.email);
        localStorage.setItem('operatorLogo', response.operator.operatorLogo || '');
        localStorage.setItem('sessionToken', response.operator.sessionToken);
        await Parse.User.become(response.operator.sessionToken);

        // Handle remember me — persist username + encrypted password so the
        // login form can be prefilled next time (or clear them when unchecked).
        if (formData.rememberMe) {
          const encryptedPassword = CryptoJS.AES.encrypt(formData.password, SECRET_KEY).toString();
          localStorage.setItem('rememberedUsername', formData.usernameOrEmail);
          localStorage.setItem('rememberedPassword', encryptedPassword);
        } else {
          localStorage.removeItem('rememberedUsername');
          localStorage.removeItem('rememberedPassword');
        }

        // Navigate to the default landing page (first authorized module).
        navigate('/player-account/search');
      } catch (error) {
        const errorMsg = error?.message || error?.toString() || 'Login failed. Please try again.';
        setErrorMessage(errorMsg);
        console.error('Login error:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#1A1A1A',
        backgroundImage: 'url(/Assets/Images/login.svg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        p: 2
      }}
    >
      <Card
        sx={{
          maxWidth: 450,
          width: '100%',
          backgroundColor: '#000000',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '12px'
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* Error Message */}
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Username/Email Field */}
              <Box>
                <Typography
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '14px',
                    mb: 1,
                    fontWeight: 500
                  }}
                >
                  Username / Email
                </Typography>
                <TextField
                  fullWidth
                  name="usernameOrEmail"
                  placeholder="Enter your username or email"
                  value={formData.usernameOrEmail}
                  onChange={handleChange}
                  error={!!errors.usernameOrEmail}
                  helperText={errors.usernameOrEmail}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#2B2B2B',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.1)'
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.2)'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#FFC107'
                      }
                    },
                    '& .MuiOutlinedInput-input': {
                      color: '#fff'
                    }
                  }}
                />
              </Box>

              {/* Password Field */}
              <Box>
                <Typography
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '14px',
                    mb: 1,
                    fontWeight: 500
                  }}
                >
                  Password
                </Typography>
                <TextField
                  fullWidth
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  error={!!errors.password}
                  helperText={errors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleTogglePassword}
                          edge="end"
                          sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#2B2B2B',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.1)'
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.2)'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#FFC107'
                      }
                    },
                    '& .MuiOutlinedInput-input': {
                      color: '#fff'
                    }
                  }}
                />
              </Box>

              {/* Remember Me */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="rememberMe"
                      checked={formData.rememberMe}
                      onChange={handleChange}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        '&.Mui-checked': {
                          color: '#FFC107'
                        }
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
                      Remember Me
                    </Typography>
                  }
                />
              </Box>

              {/* Login Button */}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{
                  backgroundColor: '#FFC107',
                  color: '#000',
                  fontWeight: 600,
                  fontSize: '16px',
                  py: 1.5,
                  textTransform: 'uppercase',
                  '&:hover': {
                    backgroundColor: '#FFD54F'
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(255, 193, 7, 0.5)',
                    color: 'rgba(0, 0, 0, 0.5)'
                  }
                }}
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Login;
